import { Sample } from 'src/app/models/sample.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { FieldRenderType } from 'src/app/shared/form/formFieldType.enum';
import { BulkEditCategory, BulkEditField } from './bulk-edit-samples-dialog.models';

export const BULK_EDIT_LIMITS = {
  MAX_FIELDS: 3,
  UPDATE_CONCURRENCY: 5,
  RESULT_STEP_DELAY_MS: 400,
  MAX_DISPLAY_VALUE_LENGTH: 20,
} as const;

export const BULK_EDIT_FIELD_CATEGORY = {
  STATUS_WORKFLOW: 'Status & Workflow',
  ASSIGNMENT: 'Assignment',
  SAMPLE_DETAILS: 'Sample Details',
  MEASUREMENTS: 'Measurements'
} as const;
export const BULK_EDIT_SURVEY_STATUS_FILTER = '0,1';

export const BULK_EDIT_EDITABLE_FIELD_IDS = new Set<string>([
  nameOf<Sample>(x => x.sampleStatus),
  nameOf<Sample>(x => x.voidReason),
  nameOf<Sample>(x => x.organizationLevelId),
  nameOf<Sample>(x => x.sampleSurveyId),
  nameOf<Sample>(x => x.segId),
  nameOf<Sample>(x => x.sampledByUserId),
  nameOf<Sample>(x => x.managingHygienistId),
  nameOf<Sample>(x => x.sampleType),
  nameOf<Sample>(x => x.sampleCategory),
  nameOf<Sample>(x => x.sampleMediaId),
  nameOf<Sample>(x => x.unsampledTimeCorrection),
  nameOf<Sample>(x => x.samplePreCal),
  nameOf<Sample>(x => x.samplePostCal),
  nameOf<Sample>(x => x.humidityPercent),
  nameOf<Sample>(x => x.pressureMmhg),
  nameOf<Sample>(x => x.startDate),
  nameOf<Sample>(x => x.endDate),
]);

export const BULK_EDIT_FIELD_CONSTRAINTS: { [fieldId: string]: { min?: number; max?: number } } = {
  [nameOf<Sample>(x => x.samplePreCal)]: { min: 0 },
  [nameOf<Sample>(x => x.samplePostCal)]: { min: 0 },
  [nameOf<Sample>(x => x.humidityPercent)]: { min: 0, max: 100 },
  [nameOf<Sample>(x => x.pressureMmhg)]: { min: 0 },
};

export const BULK_EDIT_NUMERIC_ENUM_FIELDS = new Set<string>([
  nameOf<Sample>(x => x.sampleType),
  nameOf<Sample>(x => x.sampleCategory),
  nameOf<Sample>(x => x.unsampledTimeCorrection),
  nameOf<Sample>(x => x.sampleStatus),
]);

export function getBulkEditFieldCategories(): BulkEditCategory[] {
  return [
    {
      name: BULK_EDIT_FIELD_CATEGORY.STATUS_WORKFLOW,
      icon: 'swap_horiz',
      fields: [
        {
          id: nameOf<Sample>(x => x.sampleStatus),
          name: 'Status',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.STATUS_WORKFLOW,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.voidReason),
          name: 'Void Reason',
          type: FieldRenderType.TEXTAREA,
          category: BULK_EDIT_FIELD_CATEGORY.STATUS_WORKFLOW,
          placeholder: 'Enter reason'
        },
      ]
    },
    {
      name: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
      icon: 'people',
      fields: [
        {
          id: nameOf<Sample>(x => x.organizationLevelId),
          name: 'Sampling Site',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.sampleSurveyId),
          name: 'Survey Name',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.segId),
          name: 'SEG',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.sampledByUserId),
          name: 'Sampled By',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.managingHygienistId),
          name: 'Managing Hygienist',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.ASSIGNMENT,
          options: []
        },
      ]
    },
    {
      name: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS,
      icon: 'science',
      fields: [
        {
          id: nameOf<Sample>(x => x.sampleType),
          name: 'Sample Type',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.sampleCategory),
          name: 'Sample Category',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.sampleMediaId),
          name: 'Media',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.unsampledTimeCorrection),
          name: 'Unsampled Time Correction',
          type: FieldRenderType.SELECT,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS,
          options: []
        },
        {
          id: nameOf<Sample>(x => x.startDate),
          name: 'Date and Time of Sampling',
          type: FieldRenderType.DATE_TIME,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS
        },
        {
          id: nameOf<Sample>(x => x.endDate),
          name: 'End Time',
          type: FieldRenderType.TIME,
          category: BULK_EDIT_FIELD_CATEGORY.SAMPLE_DETAILS
        },
      ]
    },
    {
      name: BULK_EDIT_FIELD_CATEGORY.MEASUREMENTS,
      icon: 'speed',
      fields: [
        {
          id: nameOf<Sample>(x => x.samplePreCal),
          name: 'Initial Flow Rate',
          type: FieldRenderType.NUMBER,
          category: BULK_EDIT_FIELD_CATEGORY.MEASUREMENTS,
          unit: ''
        },
        {
          id: nameOf<Sample>(x => x.samplePostCal),
          name: 'Final Flow Rate',
          type: FieldRenderType.NUMBER,
          category: BULK_EDIT_FIELD_CATEGORY.MEASUREMENTS,
          unit: ''
        },
        {
          id: nameOf<Sample>(x => x.humidityPercent),
          name: 'Humidity',
          type: FieldRenderType.NUMBER,
          category: BULK_EDIT_FIELD_CATEGORY.MEASUREMENTS,
          unit: '%'
        },
        {
          id: nameOf<Sample>(x => x.pressureMmhg),
          name: 'Pressure',
          type: FieldRenderType.NUMBER,
          category: BULK_EDIT_FIELD_CATEGORY.MEASUREMENTS,
          unit: 'mmHg'
        },
      ]
    },
  ];
}



